import React, { useEffect, useRef, useState } from 'react'
import { QuestionType, sendAnswers, SendAnswersRequest } from 'src/apis/gameApi'
import GameBtn from '../GameBtn'
import { AlertCard, HeaderCard, Tyre, StickyNote, AnswerContainer } from '../styles/mobile-styles'
import { useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import Timer from '../Timer'
import { toast } from 'react-toastify'
import { useWindowSize } from 'src/utils/useWindowSize'

interface DroppedTyre {
  id: string
  value: number
  x: number
  y: number
}

interface QuestionProps {
  maxAnswers?: number
  data: QuestionType
  onSubmit?: (step: number) => void
}

export default function Question({ maxAnswers = 5, data, onSubmit }: QuestionProps) {
  const step = useSelector((state: any) => state.game.step)
  const [droppedTyres, setDroppedTyres] = useState<DroppedTyre[]>([])
  const [draggedTyre, setDraggedTyre] = useState<number | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const canvasRef = useRef<HTMLDivElement>(null)

  // Touch event state for mobile support
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null)
  const [touchedElement, setTouchedElement] = useState<number | null>(null)
  const [currentTouchPosition, setCurrentTouchPosition] = useState<{ x: number; y: number } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Window size for responsive dimensions
  const { windowWidth } = useWindowSize()

  // Get dimensions based on screen size
  const getDimensions = () => {
    const isDesktop = windowWidth && windowWidth >= 1140
    if (useStickyNotes) {
      return {
        itemWidth: 120,
        itemHeight: 120,
        offsetX: 60,
        offsetY: 60,
      }
    } else {
      return {
        itemWidth: isDesktop ? 70 : 50,
        itemHeight: isDesktop ? 70 : 50,
        offsetX: isDesktop ? 35 : 25,
        offsetY: isDesktop ? 35 : 25,
      }
    }
  }

  // Determine UI style - alternating between sticky notes and tyres
  const useStickyNotes = step % 2 !== 1 // Odd steps use sticky notes, even steps use tyres

  // API call to submit answers
  const submitAnswers = useMutation({
    mutationFn: async (data: SendAnswersRequest) => {
      return await sendAnswers(data)
    },
    onSuccess: () => {
      console.log('Answers submitted successfully')
      // Notify parent to show result
      if (onSubmit) {
        onSubmit(step)
      }
    },
    onError: (error) => {
      console.error('Error submitting answers:', error)
      // Show error message to user
    },
  })

  const convertToAlphabet = (num: number) => {
    return String.fromCharCode(65 + num) // 65 is ASCII code for 'A'
  }

  const handleDragStart = (e: React.DragEvent, tyreValue: number, isFromAnswerBox: boolean = false) => {
    setDraggedTyre(tyreValue)
    e.dataTransfer.setData('text/plain', tyreValue.toString())
    e.dataTransfer.setData('application/json', JSON.stringify({ tyreValue, isFromAnswerBox }))
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedTyre(null)
  }

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent, tyreValue: number, isFromAnswerBox: boolean = false) => {
    e.preventDefault() // Prevent default touch behavior
    const touch = e.touches[0]
    setTouchStart({ x: touch.clientX, y: touch.clientY })
    setTouchedElement(tyreValue)
    setCurrentTouchPosition({ x: touch.clientX, y: touch.clientY })
    setDraggedTyre(tyreValue) // Visual feedback
    setIsDragging(false) // Will be set to true on first move

    // If dragging from answer box, we need to handle differently
    if (isFromAnswerBox) {
      // Don't prevent default click behavior initially
      e.stopPropagation()
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    console.log('Touch move detected')
    e.preventDefault() // Prevent scrolling while dragging
    if (!touchStart || !touchedElement) return

    const touch = e.touches[0]

    // Update current touch position for drag preview
    setCurrentTouchPosition({ x: touch.clientX, y: touch.clientY })

    // Set dragging state if we've moved enough
    if (!isDragging) {
      const deltaX = Math.abs(touch.clientX - touchStart.x)
      const deltaY = Math.abs(touch.clientY - touchStart.y)
      if (deltaX > 5 || deltaY > 5) {
        // Start dragging after 5px movement
        setIsDragging(true)
      }
    }

    const canvas = canvasRef.current
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      // Check if touch is within canvas bounds to show drag-over effect
      if (touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        setIsDragOver(true)
        console.log('Touch over answer box')
      } else {
        setIsDragOver(false)
      }
    }
  }

  const handleTouchEnd = (e: React.TouchEvent, isFromAnswerBox: boolean = false) => {
    console.log('Touch end detected')
    if (!touchStart || !touchedElement) return

    const touch = e.changedTouches[0]
    const canvas = canvasRef.current

    // If we haven't moved much, treat it as a click (not a drag)
    const deltaX = Math.abs(touch.clientX - touchStart.x)
    const deltaY = Math.abs(touch.clientY - touchStart.y)
    const isClick = deltaX < 10 && deltaY < 10

    // Handle click on items in answer box
    if (isFromAnswerBox && isClick && !isDragging) {
      // Find the tyre that was clicked and remove it
      const tyreToRemove = droppedTyres.find((tyre) => tyre.value === touchedElement)
      if (tyreToRemove) {
        removeTyreFromAnswerBox(tyreToRemove.id)
      }

      // Reset touch state
      setTouchStart(null)
      setTouchedElement(null)
      setCurrentTouchPosition(null)
      setIsDragging(false)
      setDraggedTyre(null)
      setIsDragOver(false)
      return
    }

    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      console.log('Touch end position:', touch.clientX, touch.clientY, 'Canvas bounds:', rect)

      // Check if touch ended within canvas bounds
      const isWithinCanvas = touch.clientX >= rect.left && touch.clientX <= rect.right && touch.clientY >= rect.top && touch.clientY <= rect.bottom

      if (isWithinCanvas) {
        console.log('Touch dropped in answer box!')
        const x = touch.clientX - rect.left
        const y = touch.clientY - rect.top
        const tyreValue = touchedElement

        // Check if tyre already exists in answer box
        const existingTyreIndex = droppedTyres.findIndex((tyre) => tyre.value === tyreValue)

        if (existingTyreIndex >= 0) {
          // Update position of existing tyre
          const { itemWidth, itemHeight, offsetX, offsetY } = getDimensions()

          setDroppedTyres((prev) =>
            prev.map((tyre, index) =>
              index === existingTyreIndex
                ? {
                    ...tyre,
                    x: Math.max(0, Math.min(x - offsetX, rect.width - itemWidth)),
                    y: Math.max(0, Math.min(y - offsetY, rect.height - itemHeight)),
                  }
                : tyre
            )
          )
        } else {
          // Check if we've reached the maximum number of answers
          if (droppedTyres.length >= maxAnswers) {
            console.log(`Maximum ${maxAnswers} answer(s) allowed`)
          } else {
            // Add new tyre
            const { itemWidth, itemHeight, offsetX, offsetY } = getDimensions()

            const newTyre: DroppedTyre = {
              id: `tyre-${tyreValue}-${Date.now()}`,
              value: tyreValue,
              x: Math.max(0, Math.min(x - offsetX, rect.width - itemWidth)),
              y: Math.max(0, Math.min(y - offsetY, rect.height - itemHeight)),
            }
            setDroppedTyres((prev) => [...prev, newTyre])
          }
        }
      } else if (isFromAnswerBox && isDragging) {
        // If dragged outside answer box from answer box, remove the item
        console.log('Item dragged outside answer box, removing...')
        const tyreToRemove = droppedTyres.find((tyre) => tyre.value === touchedElement)
        if (tyreToRemove) {
          removeTyreFromAnswerBox(tyreToRemove.id)
        }
      }
    }

    // Reset touch state
    setTouchStart(null)
    setTouchedElement(null)
    setCurrentTouchPosition(null)
    setIsDragging(false)
    setDraggedTyre(null)
    setIsDragOver(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Only set drag over to false if we're leaving the canvas area completely
    if (canvasRef.current && !canvasRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const tyreValue = parseInt(e.dataTransfer.getData('text/plain'))
    let dragData
    try {
      dragData = JSON.parse(e.dataTransfer.getData('application/json'))
    } catch {
      dragData = { tyreValue, isFromAnswerBox: false }
    }

    const canvas = canvasRef.current

    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Check if tyre already exists in answer box
      const existingTyreIndex = droppedTyres.findIndex((tyre) => tyre.value === tyreValue)

      if (existingTyreIndex >= 0) {
        // Update position of existing tyre (repositioning within answer box)
        const { itemWidth, itemHeight, offsetX, offsetY } = getDimensions()

        setDroppedTyres((prev) =>
          prev.map((tyre, index) =>
            index === existingTyreIndex
              ? {
                  ...tyre,
                  x: Math.max(0, Math.min(x - offsetX, rect.width - itemWidth)),
                  y: Math.max(0, Math.min(y - offsetY, rect.height - itemHeight)),
                }
              : tyre
          )
        )
      } else {
        // Check if we've reached the maximum number of answers
        if (droppedTyres.length >= maxAnswers) {
          console.log(`Maximum ${maxAnswers} answer(s) allowed`)
          return
        }

        // Add new tyre
        const { itemWidth, itemHeight, offsetX, offsetY } = getDimensions()

        const newTyre: DroppedTyre = {
          id: `tyre-${tyreValue}-${Date.now()}`,
          value: tyreValue,
          x: Math.max(0, Math.min(x - offsetX, rect.width - itemWidth)),
          y: Math.max(0, Math.min(y - offsetY, rect.height - itemHeight)),
        }
        setDroppedTyres((prev) => [...prev, newTyre])
      }
    }
  }

  const removeTyreFromAnswerBox = (tyreId: string) => {
    setDroppedTyres((prev) => prev.filter((tyre) => tyre.id !== tyreId))
  }

  const handleConfirmAnswer = () => {
    // Prevent multiple submissions while loading
    if (submitAnswers.isLoading) {
      return
    }

    if (droppedTyres.length === 0) {
      toast.info('Please select at least one answer')
      return
    }

    const answerIds = droppedTyres.map((tyre) => tyre.value).sort((a, b) => a - b)
    console.log('Selected answers:', answerIds, data)

    submitAnswers.mutate({ questionId: data.id, answerIds: answerIds })
  }

  useEffect(() => {
    // Reset state when question changes
    setDroppedTyres([])
    setDraggedTyre(null)
    setIsDragOver(false)
    setTouchStart(null)
    setTouchedElement(null)
    setCurrentTouchPosition(null)
    setIsDragging(false)
  }, [step, data])

  // Global drop zone to handle dragging outside answer box
  const handleGlobalDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleGlobalDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current

    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const isWithinCanvas = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom

      if (!isWithinCanvas) {
        // Dropped outside answer box, check if it's from answer box
        let dragData
        try {
          dragData = JSON.parse(e.dataTransfer.getData('application/json'))
        } catch {
          const tyreValue = parseInt(e.dataTransfer.getData('text/plain'))
          dragData = { tyreValue, isFromAnswerBox: false }
        }

        if (dragData.isFromAnswerBox) {
          // Remove item from answer box
          const tyreToRemove = droppedTyres.find((tyre) => tyre.value === dragData.tyreValue)
          if (tyreToRemove) {
            removeTyreFromAnswerBox(tyreToRemove.id)
          }
        }
      }
    }
  }

  return (
    <div onDragOver={handleGlobalDragOver} onDrop={handleGlobalDrop} style={{ minHeight: '100vh' }}>
      <Timer onTimeUp={() => console.log('Time is up!')} />
      <HeaderCard id="question-header-card" style={{ alignSelf: 'center' }}>
        <div>
          <h1>
            <span className="question-number">Q{step}</span>
            <span className="total-questions"> / 13</span>
            <br />
            <br />
            <span className="question-text">{data.content}</span>
          </h1>
          <p className="helper">
            Drag and drop or touch and drag to move items to the answer box
            {maxAnswers === 1 ? ' (Select 1 answer)' : ` (Select up to ${maxAnswers} answers)`}
          </p>
        </div>
      </HeaderCard>
      <div className="d-flex flex-column flex-md-row gap-3 mt-3 justify-content-center d-container">
        <AlertCard className="alert-card">
          <div className="answers-grid">
            {data.answers?.map((answer, index) => {
              const tyreValue = answer.id
              const isBeingDragged = draggedTyre === tyreValue
              const isBeingTouchDragged = isDragging && touchedElement === tyreValue
              const isInAnswerBox = droppedTyres.some((tyre) => tyre.value === tyreValue)

              if (useStickyNotes) {
                return (
                  <StickyNote
                    key={answer.id}
                    className={`${isBeingDragged || isBeingTouchDragged ? 'dragging' : ''} ${isInAnswerBox ? 'in-answer-box' : ''}`}
                    draggable={!isInAnswerBox}
                    style={{
                      touchAction: 'none',
                      opacity: isBeingTouchDragged ? 0.5 : 1,
                      transform: isBeingTouchDragged ? 'scale(0.95)' : 'none',
                    }}
                    onDragStart={!isInAnswerBox ? (e) => handleDragStart(e, tyreValue) : undefined}
                    onDragEnd={!isInAnswerBox ? handleDragEnd : undefined}
                    onTouchStart={!isInAnswerBox ? (e) => handleTouchStart(e, tyreValue) : undefined}
                    onTouchMove={!isInAnswerBox ? handleTouchMove : undefined}
                    onTouchEnd={!isInAnswerBox ? handleTouchEnd : undefined}
                  >
                    <span className="sticky-letter">{convertToAlphabet(index)}</span>
                    <span className="sticky-content">{answer.content}</span>
                  </StickyNote>
                )
              } else {
                return (
                  <AnswerContainer
                    key={answer.id}
                    style={{
                      opacity: isBeingTouchDragged ? 0.5 : 1,
                      transform: isBeingTouchDragged ? 'scale(0.95)' : 'none',
                    }}
                  >
                    <Tyre
                      className={`me-2 ${isBeingDragged || isBeingTouchDragged ? 'dragging' : ''} ${isInAnswerBox ? 'in-answer-box' : ''}`}
                      draggable={!isInAnswerBox}
                      style={{ touchAction: 'none' }}
                      onDragStart={!isInAnswerBox ? (e) => handleDragStart(e, tyreValue) : undefined}
                      onDragEnd={!isInAnswerBox ? handleDragEnd : undefined}
                      onTouchStart={!isInAnswerBox ? (e) => handleTouchStart(e, tyreValue) : undefined}
                      onTouchMove={!isInAnswerBox ? handleTouchMove : undefined}
                      onTouchEnd={!isInAnswerBox ? handleTouchEnd : undefined}
                    >
                      <span className="tyre-text">{convertToAlphabet(index)}</span>
                    </Tyre>
                    <div className="answer-content">{answer.content}</div>
                  </AnswerContainer>
                )
              }
            })}
          </div>
        </AlertCard>
        <AlertCard answerBox>
          <legend>
            <div>
              Answer Box ({droppedTyres.length}/{maxAnswers})
            </div>
          </legend>
          <div
            ref={canvasRef}
            className={`canvas ${isDragOver ? 'drag-over' : ''}`}
            style={{ touchAction: 'none' }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {droppedTyres.map((tyre) => {
              const answer = data.answers?.find((ans) => ans.id === tyre.value)
              const answerIndex = data.answers?.findIndex((ans) => ans.id === tyre.value) || 0

              if (useStickyNotes) {
                return (
                  <StickyNote
                    key={tyre.id}
                    className="dropped-sticky"
                    draggable
                    style={{
                      position: 'absolute',
                      left: `${tyre.x}px`,
                      top: `${tyre.y}px`,
                      cursor: 'pointer',
                      touchAction: 'none',
                    }}
                    onClick={() => removeTyreFromAnswerBox(tyre.id)}
                    onDragStart={(e) => handleDragStart(e, tyre.value, true)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, tyre.value, true)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={(e) => handleTouchEnd(e, true)}
                    title="Click to remove or drag to reposition/remove"
                  >
                    <span className="sticky-letter">{convertToAlphabet(answerIndex)}</span>
                    <span className="sticky-content">{answer?.content || tyre.value}</span>
                  </StickyNote>
                )
              } else {
                return (
                  <AnswerContainer
                    key={tyre.id}
                    draggable
                    style={{
                      position: 'absolute',
                      left: `${tyre.x}px`,
                      top: `${tyre.y}px`,
                      cursor: 'pointer',
                      touchAction: 'none',
                    }}
                    onClick={() => removeTyreFromAnswerBox(tyre.id)}
                    onDragStart={(e) => handleDragStart(e, tyre.value, true)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, tyre.value, true)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={(e) => handleTouchEnd(e, true)}
                    title="Click to remove or drag to reposition/remove"
                  >
                    <Tyre className="dropped-tyre">
                      <span className="tyre-text">{convertToAlphabet(answerIndex)}</span>
                    </Tyre>
                    <div className="answer-content">{answer?.content || tyre.value}</div>
                  </AnswerContainer>
                )
              }
            })}
          </div>
          <div className="d-flex justify-content-center mt-3">
            <GameBtn arrow className="blue" onClick={handleConfirmAnswer} disabled={submitAnswers.isLoading}>
              {submitAnswers.isLoading ? 'Submitting...' : 'Confirm answer'}
            </GameBtn>
          </div>
        </AlertCard>
      </div>

      {/* Mobile drag preview */}
      {isDragging && currentTouchPosition && touchedElement && (
        <div
          style={{
            position: 'fixed',
            left: currentTouchPosition.x - getDimensions().offsetX,
            top: currentTouchPosition.y - getDimensions().offsetY,
            pointerEvents: 'none',
            zIndex: 1000,
            opacity: 0.8,
            transform: 'rotate(-5deg)',
          }}
        >
          {useStickyNotes ? (
            <StickyNote className="dragging-preview">
              <span className="sticky-letter">{convertToAlphabet(data.answers?.findIndex((ans) => ans.id === touchedElement) || 0)}</span>
              <span className="sticky-content">{data.answers?.find((ans) => ans.id === touchedElement)?.content || ''}</span>
            </StickyNote>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tyre className="dragging-preview">
                <span className="tyre-text">{convertToAlphabet(data.answers?.findIndex((ans) => ans.id === touchedElement) || 0)}</span>
              </Tyre>
              <div className="answer-content" style={{ marginLeft: '8px', maxWidth: '200px' }}>
                {data.answers?.find((ans) => ans.id === touchedElement)?.content || ''}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
